/**
 * AppliedFilterPills Component
 *
 * Displays active filters as removable tags/pills inline with the header.
 * Shows when the search overlay is collapsed and filters are applied.
 */

import { useCallback } from 'react';
import { InlineStack, Tag } from '@shopify/polaris';

/**
 * Individual applied filter pill data
 */
export interface AppliedFilterPill {
  /** Unique key for the filter */
  key: string;
  /** Display label (e.g., "Severity: Critical") */
  label: string;
}

export interface AppliedFilterPillsProps {
  /** List of applied filters to display as pills */
  filters: AppliedFilterPill[];
  /** Handler when a filter pill is removed */
  onRemove: (key: string) => void;
}

/**
 * AppliedFilterPills - Displays active filters as removable tags
 */
export function AppliedFilterPills({ filters, onRemove }: AppliedFilterPillsProps) {
  const handleRemove = useCallback(
    (key: string) => () => {
      onRemove(key);
    },
    [onRemove]
  );

  if (filters.length === 0) {
    return null;
  }

  return (
    <InlineStack gap="100" wrap>
      {filters.map((filter) => (
        <div key={filter.key} className="badge-enter">
          <Tag onRemove={handleRemove(filter.key)}>{filter.label}</Tag>
        </div>
      ))}
    </InlineStack>
  );
}

export default AppliedFilterPills;
